/**
 * useBackgroundMusic — lit une musique de fond en boucle, à faible volume, sous la
 * narration des explicatifs. Fondu doux à l'entrée/sortie pour éviter les à-coups.
 *
 * - `play` (bool) : joue quand vrai, met en pause (en fondu) quand faux.
 * - `volume` (0→1) : volume cible (bas par défaut, pour ne pas couvrir la voix).
 * - Dégradation gracieuse : si le fichier est absent/illisible ou si l'autoplay est
 *   bloqué, on échoue silencieusement (l'explicatif reste pleinement utilisable).
 *
 * L'élément <audio> est créé une seule fois par source et réutilisé (le bed reprend
 * là où il s'était arrêté), conformément aux politiques d'autoplay (démarrage au tap).
 */

import { useEffect, useRef } from 'react';

export function useBackgroundMusic(src, { play = false, volume = 0.15 } = {}) {
  const audioRef = useRef(null);
  const volRef = useRef(volume);

  // Miroir du volume cible dans une ref (lecture hors rendu uniquement).
  // Déclaré AVANT l'effet play/pause pour que volRef soit à jour quand il tourne.
  useEffect(() => {
    volRef.current = volume;
  }, [volume]);

  // Crée l'élément audio une fois par source.
  useEffect(() => {
    if (!src) return undefined;
    const audio = new Audio(src);
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = 0;
    audioRef.current = audio;
    return () => {
      try { audio.pause(); } catch { /* noop */ }
      audioRef.current = null;
    };
  }, [src]);

  // Joue / met en pause avec un léger fondu.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    let raf = null;
    const target = play ? volRef.current : 0;

    if (play) {
      const p = audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay bloqué : silencieux */ });
    }

    const step = () => {
      const cur = audio.volume;
      const delta = target - cur;
      if (Math.abs(delta) < 0.01) {
        audio.volume = target;
        if (!play) {
          try { audio.pause(); } catch { /* noop */ }
        }
        return;
      }
      audio.volume = Math.max(0, Math.min(1, cur + delta * 0.12));
      raf = requestAnimationFrame(step);
    };
    step();

    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [play]);
}

export default useBackgroundMusic;
